import { apiFetch } from "./api";
import { alCambiarDeSesion } from "./estadoDeSesion";

/**
 * Los residenciales públicos. La unidad de tenencia de toda la plataforma: los
 * usuarios, las solicitudes, los eventos, los mensajes y las licencias se dividen
 * por residencial.
 *
 * Qué residenciales devuelve la API NO lo decide esta capa: sale del token
 * (super_admin todos, admin los de su organización, bibliotecaria el suyo). Por eso
 * aquí no hay ningún parámetro de alcance que el cliente pueda mandar.
 */

/**
 * Estados de un residencial. `tieneCentro` es un HECHO (AVP puso un Centro
 * Educativo Tecnológico allí); `status` es una DECISIÓN nuestra (lo operamos o no).
 * Son cosas distintas: se arranca operando 10-15 de los 118 que tienen centro.
 */
export type SiteStatus = "activa" | "catalogo" | "provisional" | "inactiva";

export const STATUS_LABEL: Record<SiteStatus, string> = {
  activa: "En operación",
  catalogo: "En catálogo",
  provisional: "Por confirmar",
  inactiva: "Dado de baja",
};

export const STATUS_AYUDA: Record<SiteStatus, string> = {
  activa: "Lo estamos operando. Es el único estado que cuenta para licencias.",
  catalogo: "Existe en AVP pero todavía no lo operamos.",
  provisional: "Vino del calendario A&M; falta confirmar municipio con AVP.",
  inactiva: "Dado de baja. No aparece en selectores ni en informes.",
};

export interface Site {
  code: string;
  name: string;
  municipio: string;
  status?: SiteStatus;
  region?: string | null;
  tieneCentro?: boolean;
  unidades?: number | null;
  administrador?: string | null;
  direccion?: string | null;
  telefono?: string | null;
  hasInternet?: boolean;
}

/** Site + los agregados de `GET /sites?stats=1`. */
export interface SiteStats extends Site {
  direccion?: string | null;
  telefono?: string | null;
  hasInternet?: boolean;
  participantesActivos: number;
  participantesPendientes: number;
  bibliotecarias: number;
  equiposRegistrados: number;
  equiposDeclarados: number;
  solicitudesAbiertas: number;
  eventosProximos: number;
  ultimoUso: string | null;
  /**
   * `true` solo si ya hay una cuenta de kiosco ACTIVA para esta sede — no si
   * existe una fila a medias (`status='pendiente'`, una creación que no llegó
   * a terminar). El panel usa esto para decidir entre "Crear acceso de
   * kiosco" y "Regenerar contraseña"; ofrecer "Crear" a una sede que ya lo
   * tiene es un 409 en la cara de la bibliotecaria.
   */
  tieneKiosco: boolean;
}

let cache: Site[] | null = null;
// Se olvida al cambiar de sesión: esta caché es del módulo y sobrevive al
// logout, así que sin esto el siguiente en usar el navegador vería lo del
// anterior. Ver lib/estadoDeSesion.ts.
alCambiarDeSesion(() => { cache = null; });

/**
 * Sedes para los SELECTORES: solo las que están en operación. Cacheada.
 *
 * Desde que el catálogo trae los 329 de AVP, devolver todas llenaría cada
 * desplegable de Usuarios, Eventos y Mensajes con residenciales donde no se puede
 * crear nada. El backend ya aplica ese default; aquí no se pide estado a propósito.
 */
export async function loadSites(): Promise<Site[]> {
  if (cache) return cache;
  try {
    const r = await apiFetch("/sites");
    if (!r.ok) { falloAlCargar = true; return []; }
    const d = await r.json();
    cache = (d.sites ?? []) as Site[];
    falloAlCargar = false;
    return cache;
  } catch {
    falloAlCargar = true;
    return [];
  }
}

/**
 * Si la última carga de `loadSites` FALLÓ. Un `[]` con esto en falso es «no
 * tienes residenciales»; en verdadero, «no se pudo preguntar». Confundirlas
 * mandaba a una compañía a pedir que le asignaran residenciales que ya tenía.
 */
let falloAlCargar = false;
export function falloLaCargaDeSites(): boolean {
  return falloAlCargar;
}

/**
 * Las mismas sedes con sus métricas. Sin cachear a propósito: son números que
 * cambian (usuarios nuevos, solicitudes, equipos), y una sección que se abre para
 * mirar el estado de la red no debe enseñar el de hace media hora.
 *
 * Devuelve el ERROR en vez de tragárselo. Antes esto contestaba `[]` ante cualquier
 * fallo, y la página lo traducía a "No hay residenciales en tu alcance": un 500 del
 * servidor o un token vencido se leían como "tu cuenta no tiene sedes", que manda a
 * revisar la cuenta cuando el problema está en otro sitio.
 */
export async function loadSiteStats(status = "todos"): Promise<{ sites: SiteStats[]; error: string | null }> {
  try {
    const r = await apiFetch(`/sites?stats=1&status=${encodeURIComponent(status)}`);
    if (!r.ok) {
      const d = await r.json().catch(() => ({}));
      return { sites: [], error: d.error ? `${d.error} (HTTP ${r.status})` : `HTTP ${r.status}` };
    }
    const d = await r.json();
    return { sites: (d.sites ?? []) as SiteStats[], error: null };
  } catch (e: any) {
    return { sites: [], error: e?.message ?? "no se pudo contactar la API" };
  }
}

/** Invalida la caché de sedes (tras crear/editar una). */
export function invalidateSites(): void {
  cache = null;
}

/**
 * El residencial de quien está usando la app, si tiene uno solo.
 *
 * Una bibliotecaria o un participante reciben exactamente una sede de `GET /sites`
 * —el servidor la deduce de su token—, así que ese único elemento ES su
 * residencial. Un admin o un super_admin reciben muchas y esto devuelve `null`:
 * ellos eligen, no "tienen".
 *
 * Reemplaza al viejo `SITE.siteCode` hardcodeado, que además de mentir apuntaba a
 * un residencial inexistente y hacía fallar en silencio todo lo que lo mandaba.
 */
export async function currentSite(): Promise<Site | null> {
  const s = await loadSites();
  return s.length === 1 ? s[0] : null;
}

/**
 * Cómo se describe el alcance de quien está mirando. Es lo que el sidebar enseña
 * donde antes había un nombre de residencial fijo — que para un admin era falso: no
 * administra un residencial, administra todos.
 *
 * `kicker` es la línea pequeña de encima; `null` cuando no aporta nada. A un
 * super_admin no se le dice "Todos los residenciales": ya lo sabe, y ocupa el sitio
 * del nombre del programa. A una bibliotecaria sí se le dice cuál es el suyo.
 */
export function alcanceLabel(
  cognitoRole: string | undefined,
  sites: Site[],
  programa: string,
  /**
   * Si la petición sigue en vuelo. Sin esto, `sites: []` se leía siempre como
   * "todavía cargando" — y como `loadSites()` devuelve `[]` también cuando falla,
   * el rótulo se quedaba en "Cargando residencial…" indefinidamente con la API
   * caída. Un estado de carga que no termina nunca no es un estado de carga.
   */
  cargando = false
): { kicker: string | null; titulo: string; detalle: string } {
  if (cognitoRole === "super_admin") {
    return { kicker: null, titulo: programa, detalle: `${sites.length} residenciales · vista global` };
  }
  if (cognitoRole === "admin") {
    return {
      kicker: programa,
      titulo: "Administración de Vivienda Pública",
      detalle: `${sites.length} residenciales públicos`,
    };
  }
  // Bibliotecaria y participante: su residencial. Si la API aún no respondió, no
  // se inventa un nombre — un rótulo genérico es honesto; uno equivocado no.
  const s = sites[0];
  if (s) return { kicker: programa, titulo: s.name, detalle: `${s.municipio} - ${s.name}` };
  // Ya contestó y no hay sede que enseñar: se dice la agencia, que sí es cierta,
  // en vez de dejar puesto un "cargando" que no va a cambiar.
  return {
    kicker: null,
    titulo: programa,
    detalle: cargando ? "Cargando residencial…" : "Administración de Vivienda Pública",
  };
}

// --- Alta y edición de residenciales (super_admin / admin) --------------------

export interface NuevoSite {
  name: string;
  municipio: string;
  region?: string;
  direccion?: string;
  telefono?: string;
  unidades?: number;
  administrador?: string;
  tieneCentro?: boolean;
  hasInternet?: boolean;
  status?: SiteStatus;
  /** Si se omite, el servidor lo deriva de municipio+nombre. */
  code?: string;
}

export async function crearSite(s: NuevoSite): Promise<{ ok: boolean; error?: string }> {
  return await escribir("/sites", "POST", s);
}

export async function editarSite(
  code: string,
  cambios: Partial<NuevoSite>
): Promise<{ ok: boolean; error?: string }> {
  return await escribir(`/sites/${encodeURIComponent(code)}`, "PATCH", cambios);
}

/**
 * Borra un residencial. El servidor solo lo permite si está VACÍO; si tiene
 * usuarios, eventos o solicitudes responde 409 y hay que darlo de baja
 * (`status: 'inactiva'`), que conserva su historial.
 */
export async function borrarSite(code: string): Promise<{ ok: boolean; error?: string }> {
  return await escribir(`/sites/${encodeURIComponent(code)}`, "DELETE");
}

// --- Acceso de kiosco --------------------------------------------------------

/**
 * `usuario` y `contrasena` viajan JUNTOS y UNA sola vez: el servidor no vuelve
 * a entregar esta contraseña (no la guarda en ningún sitio), así que este tipo
 * describe una respuesta que se muestra y se pierde, no un dato que se puede
 * volver a pedir.
 */
export interface AccesoKiosco {
  usuario: string;
  contrasena: string;
  /**
   * Si el servidor pudo cerrar las sesiones ya abiertas de esta cuenta al
   * fijar la contraseña nueva (`AdminUserGlobalSignOutCommand`). En `false`,
   * la contraseña YA cambió pero alguien con la sesión anterior abierta puede
   * seguir dentro hasta que su refresh token expire solo (hasta 30 días) — el
   * diálogo tiene que decirlo, no dar la regeneración por completa.
   */
  sesionesCerradas: boolean;
}

/**
 * Crea el acceso de kiosco de un residencial. Es REANUDABLE del lado del
 * servidor: si una sede quedó a medias por un fallo anterior, llamar de nuevo
 * retoma esa creación en vez de chocar con un 409 — el 409 es solo para una
 * sede que YA tiene el kiosco activo (ver `tieneKiosco` en `SiteStats`).
 */
export async function crearAccesoKiosco(
  code: string
): Promise<{ ok: boolean; acceso?: AccesoKiosco; error?: string }> {
  const r = await escribir(`/sites/${encodeURIComponent(code)}/kiosco`, "POST");
  return r.ok ? { ok: true, acceso: r.data as AccesoKiosco } : { ok: false, error: r.error };
}

/**
 * Cambia la contraseña de un kiosco que YA existe — no crea nada. Si la sede
 * no tiene acceso de kiosco, el servidor responde 404 y aquí se devuelve tal
 * cual; el panel no debería llegar a llamar esto sin que `tieneKiosco` sea
 * `true`, pero esta función no lo asume.
 */
export async function regenerarContrasenaKiosco(
  code: string
): Promise<{ ok: boolean; acceso?: AccesoKiosco; error?: string }> {
  const r = await escribir(`/sites/${encodeURIComponent(code)}/kiosco/contrasena`, "POST");
  return r.ok ? { ok: true, acceso: r.data as AccesoKiosco } : { ok: false, error: r.error };
}

/**
 * `data` viaja en las dos ramas (éxito y error) — no solo `{ ok, error }| —
 * porque `crearAccesoKiosco` y `regenerarContrasenaKiosco` necesitan el cuerpo
 * de la respuesta (`{ usuario, contrasena }`) y no solo saber si funcionó.
 * Los llamantes que no lo necesitan (crearSite, editarSite, borrarSite) lo
 * ignoran sin más.
 */
async function escribir(path: string, method: string, body?: unknown) {
  try {
    const r = await apiFetch(path, {
      method,
      headers: body ? { "content-type": "application/json" } : undefined,
      body: body ? JSON.stringify(body) : undefined,
    });
    const d = await r.json().catch(() => ({}));
    if (!r.ok) return { ok: false as const, error: d.error ?? `HTTP ${r.status}`, data: undefined };
    invalidateSites();
    return { ok: true as const, error: undefined, data: d };
  } catch (e: any) {
    return { ok: false as const, error: e?.message ?? "sin conexión", data: undefined };
  }
}

/** Municipios presentes, para agrupar y filtrar. */
export function municipios(sites: Site[]): string[] {
  return [...new Set(sites.map((s) => s.municipio))].sort((a, b) => a.localeCompare(b, "es"));
}

/**
 * Las compañías administradoras presentes en la lista.
 *
 * Salen de los propios residenciales y no de un endpoint aparte: cada uno trae su
 * `administrador`, y 335 filas dan las 18 compañías sin pedir nada más. La lista
 * es además la de verdad — si mañana AVP contrata a otra, aparece sola en cuanto
 * un residencial la lleve.
 */
export function administradores(sites: Site[]): string[] {
  const vistos = sites.map((s) => (s.administrador ?? "").trim()).filter(Boolean);
  return [...new Set(vistos)].sort((a, b) => a.localeCompare(b, "es"));
}

/**
 * La forma de comparar dos nombres de compañía: sin mayúsculas, acentos,
 * puntos ni comas, y con los espacios colapsados.
 *
 * Es la MISMA regla que `claveCompania` en el servidor (backend/lambda/api/
 * index.ts). Aquí sirve para avisar antes de guardar; allí, para que un cliente
 * que no avise tampoco pueda crear una gemela. Si cambia una, cambia la otra.
 */
export function claveCompania(nombre: string): string {
  return nombre
    .normalize("NFD").replace(/\p{Diacritic}/gu, "")
    .toLowerCase()
    .replace(/[.,]/g, "")
    .trim().replace(/\s+/g, " ");
}

export type CompaniaResuelta =
  | { tipo: "vacia" }
  | { tipo: "existente"; nombre: string }
  | { tipo: "nueva"; nombre: string };

/**
 * Qué pasa con lo que alguien teclea en «Otra compañía…»: nada (vacío), se usa
 * una que ya existe con otra grafía, o de verdad es una compañía nueva.
 *
 * El nombre de una nueva sale limpio (sin espacios de más) porque es el que se
 * va a guardar y el que verán todos en el filtro.
 */
export function resolverCompania(texto: string, existentes: string[]): CompaniaResuelta {
  const nombre = texto.trim().replace(/\s+/g, " ");
  if (!nombre) return { tipo: "vacia" };
  const clave = claveCompania(nombre);
  const igual = existentes.find((e) => claveCompania(e) === clave);
  return igual ? { tipo: "existente", nombre: igual } : { tipo: "nueva", nombre };
}
